import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-moment';
import { CookieService } from 'ngx-shared-services';
import { environment } from '../../../../environments/environment';
import { ChipModel } from '../../../../models/chip.model';
import { BannerDetails, PerformanceInterval } from '../../../../models/performance-chart.model';
import { ProgramOrganizationType } from '../../../../models/program-organization.model';
import * as Actions from '../../../../store/actions/pd.actions';
import * as Selectors from '../../../../store/selectors/pd.selectors';
import { BaseCardComponent } from '../../../shared/components/base-card/base-card.component';
import { ChipsComponent } from '../../../shared/components/chips/chips.component';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { LocaleDatePipe } from '../../../shared/pipes/locale-date.pipe';
import { Locale, LocaleTimeUtils } from '../../../shared/utils/locale-time.utils';
import { ChartDatasets, PerformanceIntervalConfig } from './performance-interval.options';
import moment from 'moment';
import { PerformanceEvent } from '../../../../models/performance-event.model';

Chart.register(...registerables, PerformanceIntervalConfig.verticalHoverLine(), PerformanceIntervalConfig.annotationBoxPlugin());

@Component({
  selector: 'fdr-past-performance-ui-performance-interval',
  standalone: true,
  imports: [CommonModule, BaseCardComponent, ChipsComponent, TranslateModule, EmptyStateComponent, LocaleDatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './performance-interval.component.html',
  styleUrls: ['./performance-interval.component.scss'],
})
export class PerformanceIntervalComponent implements OnInit, AfterViewInit, OnDestroy {
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private cookieService = inject(CookieService);
  readonly locale = this.cookieService.getI18NLocale() || environment.defaults.locale;

  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;
  private chart: Chart | null = null;
  performanceData?: PerformanceInterval[];
  performanceIntervalData$ = this.store.select(Selectors.selectSitePerformanceIntervalData);

  readonly intervalData = this.store.selectSignal(Selectors.selectSitePerformanceIntervalData);
  readonly isIntervalLoading = this.store.selectSignal(Selectors.selectSitePerformanceIntervalDataLoading);

  private readonly eventsDetails = this.store.selectSignal(Selectors.selectSitePerformanceEvents);
  private readonly viewMode = this.store.selectSignal(Selectors.selectViewMode);
  readonly isOrgView = computed(() => this.viewMode() === ProgramOrganizationType.Organization);

  /** The selected event, saved to store when the user navigates from the table. */
  readonly selectedEvent = this.store.selectSignal(Selectors.selectSelectedEvent);

  private readonly organizations = this.store.selectSignal(Selectors.selectOrganizations);
  private readonly products = this.store.selectSignal(Selectors.selectProducts);
  readonly eventTimeZone = computed(() => this.selectedEvent()?.timezone || 'UTC');

  readonly productName = computed(() => {
    const event = this.selectedEvent();
    if (!event) return '';
    const fromList = this.products().find(p => p.id === event.productId)?.name;
    return fromList || event.productName || '';
  });

  readonly bannerDetails = computed<BannerDetails | null>(() => {
    const event = this.selectedEvent();
    if (!event) return null;
    return this.createBanner(event);
  });

  readonly showProductRow = LocaleTimeUtils.matchesLocale(this.locale, Locale.ES_ES);

  readonly organizationName = computed(() => {
    const event = this.selectedEvent();
    if (!event) return '';
    return this.organizations().find(o => o.id === event.organizationId)?.name ?? event.organizationName ?? '';
  });

  readonly selectedEventDuration = computed(() => {
    const { eventStartDttm, eventEndDttm } = this.selectedEvent() ?? {};
    if (!eventStartDttm || !eventEndDttm) return null;

    const diffMs = new Date(eventEndDttm).getTime() - new Date(eventStartDttm).getTime();
    const totalMinutes = Math.floor(diffMs / 60_000);
    const hours = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, '0');
    const minutes = (totalMinutes % 60).toString().padStart(2, '0');

    return `${hours}:${minutes} h`;
  });

  private readonly langChange = toSignal(this.translate.onLangChange, { initialValue: null });

  readonly chipsInfo = computed<ChipModel[]>(() => {
    this.langChange();
    return [
      { label: this.translate.instant('PD.PERFORMANCE.CHART_LABELS.BASELINE'), color: '#F55600', show: true },
      { label: this.translate.instant('PD.PERFORMANCE.CHART_LABELS.DEMAND'), color: '#461E7D', show: true },
      { label: this.translate.instant('PD.PERFORMANCE.CHART_LABELS.TARGET'), color: '#35998F', show: true },
    ];
  });

  constructor() {
    effect(() => {
      const event = this.selectedEvent();
      if (!event) return;

      untracked(() => {
        if (this.viewMode() === ProgramOrganizationType.Program && event.productId) {
          this.store.dispatch(
            Actions.loadProductPerformanceIntervalData({
              productId: event.productId,
              eventId: event.eventId,
            })
          );
          return;
        }

        this.store.dispatch(
          Actions.loadSitePerformanceIntervalData({
            siteId: event.siteId,
            eventId: event.eventId,
          })
        );
      });
    });
  }

  ngOnInit(): void {
    this.loadDataFromAssets();
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  ngAfterViewInit(): void {
    if (this.performanceData) {
      this.createChart();
    }
  }

  private loadDataFromAssets(): void {
    this.performanceIntervalData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: PerformanceInterval[] | undefined) => {
      if (response && response.length > 0) {
        this.performanceData = response;

        if (this.chartCanvas) {
          this.createChart();
        }
      }
    });
  }

  private createChart(): void {
    if (!this.performanceData || !this.chartCanvas) {
      return;
    }

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      return;
    }

    if (this.chart) {
      this.chart.destroy();
    }

    // Transform data using the config helper
    const datasets: ChartDatasets = PerformanceIntervalConfig.transformData(this.performanceData);
    const event = this.selectedEvent();
    const boundaries = event
      ? [event.eventStartDttm, event.eventEndDttm].map(value => moment(value).valueOf()).filter(Number.isFinite)
      : [];

    // Get chart configuration
    const config = PerformanceIntervalConfig.getChartConfig(datasets, this.chipsInfo(), this.locale, this.eventTimeZone(), boundaries);

    // Create chart
    this.chart = new Chart(ctx, config);
    this.createRampPeriod();
    this.chart.update();
  }

  disableChip(chipIndex: number): void {
    if (!this.chart) return;

    const dataset = this.chart.data.datasets[chipIndex];
    if (dataset) {
      dataset.hidden = !dataset.hidden;
      this.chart.update();
    }

    if (this.chipsInfo()[chipIndex]) {
      this.chipsInfo()[chipIndex].show = !this.chipsInfo()[chipIndex].show;
    }
  }

  private createRampPeriod() {
    const event = this.selectedEvent();
    if (!event) return;
    const firstValue = this.performanceData ? this.performanceData[0] : undefined;
    const lastValue = this.performanceData ? this.performanceData[this.performanceData.length - 1] : undefined;
    if (!firstValue || !lastValue) return;

    const from = moment(firstValue.interval_dttm).valueOf() || 0;
    const to = moment(event?.eventStartDttm).valueOf() || 0;
    const start = moment(event?.eventEndDttm).valueOf() || 0;
    const end = moment(lastValue.interval_dttm).valueOf() || 0;


    if (from && to) {
      this.addPlotBand('box2', from, Math.floor(to / 60000) * 60000, 'rgb(241, 241, 242)', 3);
    }
    if (start && end) {
      this.addPlotBand('box3', start, end, 'rgb(241, 241, 242)', 2);
    }
  }

  private addPlotBand = (key: string, xMin: number, xMax: number, backgroundColor: string, zIndex: number) => {
    if (!this.chart) return;
    if (!this.chart.options) this.chart.options = {} as any;
    if (!this.chart.options.plugins) this.chart.options.plugins = {} as any;
    if (!this.chart.options.plugins!.annotation) this.chart.options.plugins!.annotation = { annotations: {} } as any;
    if (!this.chart.options.plugins!.annotation!.annotations) this.chart.options.plugins!.annotation!.annotations = {} as any;

    const annotations = this.chart.options.plugins!.annotation!.annotations as Record<string, any>;
    annotations[key] = {
      type: 'box',
      xMin: moment.utc(xMin).toISOString(),
      xMax: moment.utc(xMax).toISOString(),
      backgroundColor,
      borderWidth: 0,
      borderColor: backgroundColor,
      drawTime: 'beforeDraw',
      z: zIndex,
    };
  };

  private createBanner = (event: PerformanceEvent): BannerDetails => {




    const { eventType, eventStartDttm, eventEndDttm, performanceValue, performancePercentage, enrollmentValue, } = event;

    let type: string = 'cancel';
    let title = '';
    const description = this.translate.instant('PD.PERFORMANCE.LBL_PERFORMANCE_MESSAGE', {
      delivered: Math.round(performanceValue),
      estimated: Math.round(enrollmentValue),
      performance: Math.round(performancePercentage),
    });

    if (performancePercentage >= 100) {
      type = 'success';
      title = this.translate.instant('PD.PERFORMANCE.LBL_PERFORMANCE_HEADER1');
    } else if (performancePercentage >= 60) {
      type = 'warning';
      title = this.translate.instant('PD.PERFORMANCE.LBL_PERFORMANCE_HEADER2');
    } else {
      type = 'error';
      title = this.translate.instant('PD.PERFORMANCE.LBL_PERFORMANCE_HEADER3');
    }
    return { title, description, ...BANNER_STYLES[type] };
  }
}

const BANNER_STYLES = Object.freeze<Record<string, { backgroundColor: string; borderColor: string; iconName: string }>>({
  success: {
    backgroundColor: '#F5FFF6',
    borderColor: '#00855D',
    iconName: 'check-circle-fill',
  },
  warning: {
    backgroundColor: '#FCF1E6',
    borderColor: '#E67300',
    iconName: 'exclamation-circle-fill',
  },
  error: {
    backgroundColor: '#FFF1F2',
    borderColor: '#C8102E',
    iconName: 'exclamation-triangle-fill',
  },
  cancel: {
    backgroundColor: 'rgb(241, 241, 242)',
    borderColor: '#6B6B6B',
    iconName: 'slash-circle-fill',
  },
}); 
